import { SharePointListClient, odataEscape, type ListItem } from '../SharePointListClient';

/**
 * 00.Usuarios field shape (internal SharePoint column names).
 *
 * NOTE: Password_USR is plaintext in the legacy list. We need to write it on
 * create/update to stay compatible with the existing backend login flow
 * (which compares literally). Migrate to bcrypt hashes once we control the
 * sign-in path end-to-end.
 */
export type UsuariosFields = {
  Title?: string | null;
  Nombre_USR?: string | null;
  Apellido_USR?: string | null;
  ConcatName_USR?: string | null;
  UsuarioApp_USR?: string | null;
  Password_USR?: string | null;
  Perfil_USR?:
    | 'Admin'
    | 'Administracion'
    | 'Ventas'
    | 'Mantenimiento'
    | 'Nova'
    | 'Visitante'
    | string
    | null;
  /** Default landing country ("Argentina" / "Mexico"). When the user has
   *  more than one país permitido this is the one they start in after
   *  login; they can switch via the CountrySwitcher. */
  Pais_USR?: string | null;
  /** ISO code for `Pais_USR`. Backend uses this for data scoping. */
  Pais_Codigo_USR?: string | null;
  /** Every ISO country the user can switch into. Single-country users have
   *  exactly one entry here. Multi-country users (e.g. cross-region
   *  admins) have multiple. */
  Paises_Permitidos_USR?: ('AR' | 'MX')[] | null;
  Status_USR?: 'Activo' | 'Inactivo' | string | null;
};

export type UsuarioItem = ListItem<UsuariosFields>;

export const usuariosClient = new SharePointListClient<UsuariosFields>('Usuarios');

/** Scans all users (both Activo and Inactivo). The Configuración table shows the status badge. */
export async function listAllUsuarios(): Promise<UsuarioItem[]> {
  return usuariosClient.listItems({ top: 5000 });
}

export async function listActiveUsuarios(): Promise<UsuarioItem[]> {
  return usuariosClient.listItems({
    filter: "fields/Status_USR eq 'Activo'",
    orderBy: 'fields/ConcatName_USR asc',
  });
}

export async function findUsuarioByUsuarioApp(usuarioApp: string): Promise<UsuarioItem | null> {
  const items = await usuariosClient.listItems({
    filter: `fields/UsuarioApp_USR eq ${odataEscape(usuarioApp)} and fields/Status_USR eq 'Activo'`,
    top: 1,
  });
  return items[0] ?? null;
}

export async function createUsuario(fields: Partial<UsuariosFields>): Promise<UsuarioItem> {
  const nombre = fields.Nombre_USR?.trim() ?? '';
  return usuariosClient.createItem({
    Title: '[sumar]',
    Status_USR: 'Activo',
    ...fields,
    Nombre_USR: nombre,
    ConcatName_USR: nombre,
  });
}

export async function updateUsuario(
  id: string,
  fields: Partial<UsuariosFields>,
): Promise<UsuarioItem> {
  const patch: Partial<UsuariosFields> = { ...fields };
  if (fields.Nombre_USR !== undefined) {
    patch.ConcatName_USR = fields.Nombre_USR?.trim() ?? '';
  }
  return usuariosClient.updateItem(id, patch);
}

/** Soft-delete via Status_USR = 'Inactivo'. */
export async function softDeleteUsuario(id: string): Promise<UsuarioItem> {
  return usuariosClient.updateItem(id, { Status_USR: 'Inactivo' });
}

export async function reactivateUsuario(id: string): Promise<UsuarioItem> {
  return usuariosClient.updateItem(id, { Status_USR: 'Activo' });
}
